import { isMacOs } from '../../helpers/device';
import { focusManager } from './FocusManager';
import { stackManager } from './StackManager';
import { HOT_KEY, shouldPreventDefault } from './constants';
import type { HotKeyHandler, HotKeyRegistration } from './types';

type HotKeys = string;

export class HotKeysManager {
    private static instance: HotKeysManager;

    private cleanup?: () => void;

    private constructor() {}

    public static getInstance(): HotKeysManager {
        if (!HotKeysManager.instance) {
            HotKeysManager.instance = new HotKeysManager();
        }

        return HotKeysManager.instance;
    }

    public initialize(): void {
        if (this.cleanup) return;

        this.cleanup = this.setupGlobalListeners();
    }

    private setupGlobalListeners(): () => void {
        if (this.cleanup) {
            return this.cleanup;
        }

        const handleKeyDown = this.handleKeyDown.bind(this);

        const handleKeyUp = this.handleKeyUp.bind(this);

        window.addEventListener('keydown', handleKeyDown, {
            capture: true,
        });

        window.addEventListener('keyup', handleKeyUp, {
            capture: true,
        });

        this.cleanup = () => {
            window.removeEventListener('keydown', handleKeyDown, {
                capture: true,
            });

            window.removeEventListener('keyup', handleKeyUp, {
                capture: true,
            });

            this.cleanup = undefined;
        };

        return this.cleanup;
    }

    public register(componentId: string, handler: Omit<HotKeyHandler, 'id' | 'componentId'>): HotKeyRegistration {
        const keys = Array.isArray(handler.keys) ? handler.keys : [handler.keys];
        const firstKey = keys[0];

        const fullHandler: HotKeyHandler = {
            ...handler,
            id: this.generateId(componentId, firstKey),
            componentId,
        };

        keys.forEach(key => {
            stackManager.addHandler({
                ...fullHandler,
                keys: key,
            });
        });

        return {
            unregister: () => this.unregister(fullHandler.id),
            updateCallback: (newCallback: (event: KeyboardEvent) => void) =>
                this.updateCallback(fullHandler.id, newCallback),
        };
    }

    public unregister(handlerId: string): void {
        stackManager.removeHandlerById(handlerId);
    }

    public updateCallback(handlerId: string, callback: (event: KeyboardEvent) => void): void {
        stackManager.setHandlerCallback(handlerId, callback);
    }

    private async handleKeyDown(event: KeyboardEvent): Promise<void> {
        const key = this.normalizeKey(event);

        if (!key) return;

        const activeHandler = stackManager.getActiveHandler(key);

        if (activeHandler) {
            try {
                // Проверяем, нужно ли пропустить обработку Enter в textarea/contenteditable
                const shouldSkipEnter = this.shouldSkipEnterInEditableElement(event, key, activeHandler);

                if (shouldSkipEnter) {
                    return; // Пропускаем обработку, даём браузеру обработать Enter
                }

                // применяем preventDefault для браузерных комбинаций
                // или если явно указано в опциях
                const shouldPrevent = activeHandler.preventDefault || shouldPreventDefault(key);

                if (shouldPrevent) {
                    event.preventDefault();
                }

                if (activeHandler.stopPropagation) {
                    event.stopPropagation();
                }

                if (activeHandler.stopImmediatePropagation) {
                    event.stopImmediatePropagation();
                }

                // игнорируем удержание кнопки если repeat явно не включен в настройках activeHandler
                if (activeHandler.repeat || !event.repeat) {
                    activeHandler.callback(event);
                }

                return;
            } catch (error) {
                console.error('handle key down error: ', error);

                return;
            }
        }
    }

    private async handleKeyUp(event: KeyboardEvent): Promise<void> {
        const key = this.normalizeKey(event);

        if (!key) return;

        const activeHandler = stackManager.getActiveHandler(key);

        if (activeHandler && activeHandler.keyUp) {
            try {
                // применяем preventDefault для браузерных комбинаций
                // или если явно указано в опциях
                const shouldPrevent = activeHandler.preventDefault || shouldPreventDefault(key);
                if (shouldPrevent) {
                    event.preventDefault();
                }

                if (activeHandler.stopPropagation) {
                    event.stopPropagation();
                }

                if (activeHandler.stopImmediatePropagation) {
                    event.stopImmediatePropagation();
                }

                activeHandler.callback(event);

                return;
            } catch (error) {
                console.error('handle key up error: ', error);

                return;
            }
        }
    }

    /**
     * Проверяет, нужно ли пропустить обработку Enter в contenteditable
     * @returns true если нужно пропустить (дать браузеру обработать Enter)
     */
    private shouldSkipEnterInEditableElement(event: KeyboardEvent, key: string, handler: HotKeyHandler): boolean {
        // Проверяем только для Enter
        if (key !== 'enter') {
            return false;
        }

        const target = event.target as HTMLElement;

        // Если явно указан preventDefault, всегда обрабатываем
        if (handler.preventDefault) {
            return false;
        }

        // Проверяем contenteditable - пропускаем Enter (дать браузеру обработать новую строку)
        if (target.isContentEditable) {
            return true;
        }

        // Для остальных элементов (input, button, textarea и т.д.) обрабатываем через handler
        return false;
    }

    private normalizeKey(event: KeyboardEvent): HotKeys | null {
        // игнорируем события без клавиши
        if (!event.key) return null;

        const isMac = isMacOs();

        const key = this.extractKey(event, isMac);
        if (!key) return null;

        const hotKey = this.buildHotKey(event, key, isMac);

        return this.isValidHotKey(hotKey) ? (hotKey as HotKeys) : null;
    }

    // маппинг спецсимволвом мака
    private readonly MAC_SPECIAL_CHARS: Record<string, string> = {
        '¬': 'o', // cmd+alt+o
        œ: 'o', // cmd+alt+o (альтернативный)
        '∑': 'w', // cmd+alt+w
        '∂': 'd', // cmd+alt+d
        ƒ: 'f', // cmd+alt+f
        '©': 'c', // cmd+alt+c
        '∆': 'j', // cmd+alt+j
        Ω: 'z', // cmd+alt+z
        å: 'a', // cmd+alt+a
        ß: 's', // cmd+alt+s
        π: 'p', // cmd+alt+p
        µ: 'm', // cmd+alt+m
        '˙': 'h', // cmd+alt+h
        '¡': '1', // cmd+alt+1
        '™': '2', // cmd+alt+2
        '£': '3', // cmd+alt+3
        '¢': '4', // cmd+alt+4
        '∞': '5', // cmd+alt+5
        '§': '6', // cmd+alt+6
        '¶': '7', // cmd+alt+7
        '•': '8', // cmd+alt+8
        ª: '9', // cmd+alt+9
        º: '0', // cmd+alt+0
    };

    private extractKey(event: KeyboardEvent, isMac: boolean): string | null {
        let key: string;

        // проверяем спец символы мака
        if (isMac && this.MAC_SPECIAL_CHARS[event.key]) {
            key = this.MAC_SPECIAL_CHARS[event.key];
        } else {
            // для комбинаций с модификаторами используем event.code для букв
            const hasModifiers = event.metaKey || event.ctrlKey || event.altKey || event.shiftKey;
            if (event.code && hasModifiers) {
                const isLetter = event.code.startsWith('Key');
                key = isLetter ? event.code.toLowerCase().replace('key', '') : event.key.toLowerCase();
            } else {
                key = event.key.toLowerCase();
            }
        }

        return this.normalizeSpecialKeys(key);
    }

    private normalizeSpecialKeys(key: string): string {
        const keyMap: Record<string, string> = {
            escape: 'esc',
            ' ': 'space',
        };

        return keyMap[key] || key;
    }

    private buildHotKey(event: KeyboardEvent, key: string, isMac: boolean): string {
        const modifiers: string[] = [];

        if (event.metaKey) {
            modifiers.push('cmd');
        } else if (event.ctrlKey) {
            modifiers.push(isMac ? 'control' : 'ctrl');
        }

        if (event.altKey) modifiers.push('alt');
        if (event.shiftKey) modifiers.push('shift');

        if (modifiers.length === 0) {
            return key;
        }

        // строим финальную строку: "modifier1+modifier2+key"
        return [...modifiers, key].join('+');
    }

    private isValidHotKey(key: string): boolean {
        // Проверяем, есть ли ключ в константах HOT_KEY
        const hotKeyValues = Object.values(HOT_KEY);

        const isInHotKeys = hotKeyValues.some(hotKeyValue => {
            if (Array.isArray(hotKeyValue)) {
                return hotKeyValue.includes(key);
            }

            return hotKeyValue === key;
        });

        return (
            isInHotKeys ||
            // Проверяем комбинации клавиш
            key.includes('+') ||
            // Проверяем базовые клавиши
            [
                'esc',
                'enter',
                'space',
                'tab',
                'backspace',
                'delete',
                'arrowup',
                'arrowdown',
                'arrowleft',
                'arrowright',
            ].includes(key)
        );
    }

    private generateId(componentId: string, key: string): string {
        return `${componentId}_${key}`;
    }
}

export const hotKeysManager = HotKeysManager.getInstance();

// Debug helper - доступен в console как window.__hotKeysDebug
if (typeof window !== 'undefined') {
    (window as any).__hotKeysDebug = {
        getStackInfo: () => stackManager.getDebugInfo(),
        getStacks: () => stackManager,
        getFocusedComponent: () => focusManager.getFocusedComponentId(),
        clearFocus: () => focusManager.clearFocus(),
        simulateKeyPress: (
            key: string,
            modifiers?: { cmd?: boolean; ctrl?: boolean; alt?: boolean; shift?: boolean },
        ) => {
            const event = new KeyboardEvent('keydown', {
                key,
                metaKey: modifiers?.cmd,
                ctrlKey: modifiers?.ctrl,
                altKey: modifiers?.alt,
                shiftKey: modifiers?.shift,
                bubbles: true,
                cancelable: true,
            });
            document.dispatchEvent(event);
        },
        enableDebugMode: () => {
            localStorage.setItem('HOTKEYS_DEBUG', 'true');
            console.log('🔥 Hotkeys debug mode enabled. Reload page to see debug logs.');
        },
        disableDebugMode: () => {
            localStorage.removeItem('HOTKEYS_DEBUG');
            console.log('Hotkeys debug mode disabled.');
        },
        isDebugMode: () => localStorage.getItem('HOTKEYS_DEBUG') === 'true',
    };
}
